import type { CSSProperties, ReactNode } from 'react'
import React from 'react'
import classNames from 'classnames'
import isMobile from 'is-mobile'

import { Card } from '../Card/Card'
import type {
  PopoverOnHoverProps,
  PopoverOnHoverRef,
} from '../PopoverOnHover/PopoverOnHover'
import { PopoverOnHover } from '../PopoverOnHover/PopoverOnHover'

export type TooltipOnHoverProps = Omit<PopoverOnHoverProps, 'content'> & {
  content: string | ReactNode
  cardClassName?: string
  cardStyle?: CSSProperties
  shortcut?: React.ReactNode
  transitionDuration?: number
  ref?: React.Ref<TooltipOnHoverRef>
}

type TooltipOnHoverRef = PopoverOnHoverRef

export function TooltipOnHover({
  content,
  align = 'center',
  cardClassName,
  cardStyle,
  openDelay = 200,
  allowContentHover,
  reposition = true,
  boundaryInset = 8,
  shortcut,
  ref,
  ...props
}: TooltipOnHoverProps) {
  const disabled = props.disabled || (isMobile() && !props.openOnTouch)

  return (
    <PopoverOnHover
      {...props}
      allowContentHover={allowContentHover}
      containerStyle={{ ...props.containerStyle, pointerEvents: 'none' }}
      openDelay={openDelay}
      reposition={reposition}
      ref={ref}
      align={align}
      boundaryInset={boundaryInset}
      disabled={disabled}
      content={({ props, ref }) => {
        return (
          <Card
            ref={ref}
            {...props}
            style={{
              maxWidth: 280,
              ...cardStyle,
            }}
            className={classNames(
              'tooltip-on-hover animation-fade-in dark-mode shade-05 shadow-small',
              'font-size-medium font-weight-medium font-size-7',
              'padding-vertical-8 padding-horizontal-12',
              {
                'pointer-events-none no-selection': !allowContentHover,
                'pointer-events-all': allowContentHover,
                'center-text': align === 'center',
                'flexbox flexbox-align-center': Boolean(shortcut),
              },
              cardClassName
            )}
          >
            {content}
            {shortcut && (
              <div className="shade-30 margin-left-8">{shortcut}</div>
            )}
          </Card>
        )
      }}
    />
  )
}
